import { hasNight, loadAttendCodes, withNight, withoutNight } from '../services/attendCodes';
import { useState, useEffect, useCallback, useRef } from 'react';
import Swal from 'sweetalert2';
import timesheetService from '../services/timesheetService';
import timesheetSyncService, { EVT_SYNC_COMPLETED } from '../services/timesheetSyncService';

/**
 * React hook, инкапсулирующий всю бизнес-логику таймшита.
 * Работает ТОЛЬКО с IndexedDB (без GET-запросов к API — см. feedback).
 * Синхронизация с бэкендом будет вынесена в отдельный timesheetSyncService.
 */
export function useTimesheetData() {
    const [currentDate, setCurrentDate] = useState(() => timesheetService.toLocalDateString());
    const [records, setRecords] = useState([]);
    // День, чьи записи сейчас в records: loadDate ставит дату сразу, а записи —
    // после чтения базы. Странице нужно знать, что записи нового дня уже на месте.
    const [recordsDate, setRecordsDate] = useState(null);
    const [weekRecords, setWeekRecords] = useState([]);
    const [dailyNotes, setDailyNotes] = useState([]);
    const [weekDailyNotes, setWeekDailyNotes] = useState([]);
    const [selectedEmployeeIds, setSelectedEmployeeIdsState] = useState([]);
    const [selectedJobIds, setSelectedJobIdsState] = useState([]);
    const [weekInfo, setWeekInfo] = useState(null);

    const loadingRef = useRef(false);
    // Перечитать ещё раз, когда текущая загрузка закончится (запрос пришёл посреди неё).
    const reloadAgainRef = useRef(false);
    const isMountedRef = useRef(true);
    // Throttle week-mode pull чтобы не было loop'а EVT_SYNC_COMPLETED → loadDate → pull → ...
    const lastWeekPullRef = useRef({ week: null, at: 0 });
    // Актуальные срезы для чтения внутри useCallback без пересоздания функций
    const recordsRef = useRef([]);
    const weekRecordsRef = useRef([]);
    const dailyNotesRef = useRef([]);
    const selectedEmployeeIdsRef = useRef([]);
    const selectedJobIdsRef = useRef([]);
    const currentDateRef = useRef(currentDate);

    useEffect(() => { recordsRef.current = records; }, [records]);
    useEffect(() => { weekRecordsRef.current = weekRecords; }, [weekRecords]);
    useEffect(() => { dailyNotesRef.current = dailyNotes; }, [dailyNotes]);
    useEffect(() => { selectedEmployeeIdsRef.current = selectedEmployeeIds; }, [selectedEmployeeIds]);
    useEffect(() => { selectedJobIdsRef.current = selectedJobIds; }, [selectedJobIds]);
    useEffect(() => { currentDateRef.current = currentDate; }, [currentDate]);

    useEffect(() => {
        isMountedRef.current = true;
        return () => { isMountedRef.current = false; };
    }, []);

    const showError = (title, text) => {
        Swal.fire({
            icon: 'error',
            title,
            text,
            timer: 3500,
            showConfirmButton: true,
        });
    };

    const safeSetState = (setter) => (value) => {
        if (isMountedRef.current) setter(value);
    };

    // ─── Week pull ──────────────────────────────────────────────────────────

    /**
     * Pull недели по листу формана: свои записи + выжимка чужих часов по этим
     * же людям (колонка Other). force — сразу после правки листа: иначе по
     * только что добавленному человеку чужие часы пришли бы через 30 секунд.
     */
    const pullWeek = useCallback((date, empIds, force = false, forceFull = false) => {
        try {
            const weekEnding = timesheetService.getWeekEndingDate(date);
            const now = Date.now();
            const lastWeek = lastWeekPullRef.current.week;
            const lastAt = lastWeekPullRef.current.at || 0;
            if (!force && lastWeek === weekEnding && (now - lastAt) <= 30_000) return;
            lastWeekPullRef.current = { week: weekEnding, at: now };
            // Temps хранятся на клиенте с negative id (-tempId);
            // на сервере две колонки employee_id / temp_id — разделяем.
            timesheetSyncService.pullServerUpdates({
                weekEnding,
                employeeIds: empIds.filter(id => id > 0),
                tempIds: empIds.filter(id => id < 0).map(id => -id),
                // Локально на неделе пусто — курсору не верим, тянем неделю целиком.
                forceFull,
            }).then(changed => {
                // Неделя пришла с сервера (новое устройство, другая неделя) — таблица
                // уже нарисована из пустой локальной базы, перечитываем её. Раньше на
                // втором устройстве под той же учёткой часы с айпада появлялись только
                // после смены дня или перезагрузки.
                if (changed && typeof window !== 'undefined') {
                    window.dispatchEvent(new CustomEvent(EVT_SYNC_COMPLETED, { detail: { weekPull: true } }));
                }
            }).catch(() => {});
        } catch { /* non-fatal */ }
    }, []);

    // ─── Load data for a specific date ──────────────────────────────────────

    const loadDate = useCallback(async (date) => {
        if (loadingRef.current) { reloadAgainRef.current = true; return; }
        loadingRef.current = true;

        try {
            await loadAttendCodes();
            setCurrentDate(date);

            const [dayRecords, allWeekRecords, dayNotes, allWeekNotes, empIds, jobIds] = await Promise.all([
                timesheetService.getTimesheetRecordsByDate(date),
                timesheetService.getTimesheetRecordsByWeek(date),
                timesheetService.getDailyJobNotesByDate(date),
                timesheetService.getDailyJobNotesByWeek(date),
                timesheetService.getSelectedEmployeesWithFallback(date),
                timesheetService.getSelectedJobsWithFallback(date),
            ]);

            // Week-mode pull — throttle чтобы не было loop'а:
            //   EVT_SYNC_COMPLETED → loadDate → pull → merge → tick → EVT_SYNC_COMPLETED → ...
            // Pull за текущую неделю не чаще раза в 30 сек (кэш серверного состояния),
            // либо сразу если смена недели (weekEnding != lastPulledWeek).
            //
            // Запускаем ПОСЛЕ чтения IDB чтобы знать selectedEmployeeIds — это
            // позволит сузить серверный запрос до листа бригадира (subject-scope).
            // Без этого pull тащил бы всех сотрудников компании за неделю и
            // упирался бы в PULL_MAX_ROWS=500 на больших объёмах.
            pullWeek(date, empIds, false, allWeekRecords.length === 0);

            if (!isMountedRef.current) return;

            // Показываем только НЕ удалённые записи; удалённые нужны только sync-сервису
            const visibleDay = dayRecords.filter(r => !r.isDeleted);
            const visibleWeek = allWeekRecords.filter(r => !r.isDeleted);
            const visibleDayNotes = dayNotes.filter(n => !n.isDeleted);
            const visibleWeekNotes = allWeekNotes.filter(n => !n.isDeleted);

            safeSetState(setRecords)(visibleDay);
            safeSetState(setRecordsDate)(date);
            safeSetState(setWeekRecords)(visibleWeek);
            safeSetState(setDailyNotes)(visibleDayNotes);
            safeSetState(setWeekDailyNotes)(visibleWeekNotes);

            // Авто-добавление сотрудников/работ из СВОИХ записей недели (восстановление
            // листа на новом устройстве/после очистки данных).
            // КРИТИЧНО: только свои записи. Pull тянет записи ВСЕХ форманов компании
            // (инкрементальный pull идёт вообще без фильтра по сотрудникам), и объединение
            // по ним затягивало чужих людей и чужие работы в мой лист НАВСЕГДА.
            // Чужие часы всё равно видны и учитываются: оранжевая колонка «другие форманы»
            // (по createdByUserId) + серверный лимит 24ч по всем работам/форманам.
            const myUserId = timesheetService.getCurrentUserMeta().userId;
            const activeWeekRecs = visibleWeek.filter(r =>
                r.hours > 0 && (myUserId == null || Number(r.createdByUserId) === Number(myUserId))
            );
            const empIdSet = new Set(empIds);
            const jobIdSet = new Set(jobIds);
            const missingEmpIds = [...new Set(activeWeekRecs.map(r => r.employeeId).filter(id => !empIdSet.has(id)))];
            const missingJobIds = [...new Set(activeWeekRecs.map(r => r.jobId).filter(id => !jobIdSet.has(id)))];

            let finalEmpIds = empIds;
            let finalJobIds = jobIds;

            if (missingEmpIds.length > 0) {
                finalEmpIds = [...finalEmpIds, ...missingEmpIds];
            }
            if (finalEmpIds !== empIds) {
                await timesheetService.saveSelectedEmployeesForWeek(date, finalEmpIds);
            }
            if (missingJobIds.length > 0) {
                finalJobIds = [...jobIds, ...missingJobIds];
                await timesheetService.saveSelectedJobsForWeek(date, finalJobIds);
            }

            if (!isMountedRef.current) return;

            safeSetState(setSelectedEmployeeIdsState)(finalEmpIds);
            safeSetState(setSelectedJobIdsState)(finalJobIds);

            const weekEnd = timesheetService.getWeekEndingDate(date);
            const weekDates = timesheetService.getWeekDates(date);
            const signedTimesheets = await timesheetService.getSignedTimesheetsByWeek(weekEnd);

            const signedJobs = {};
            for (const st of signedTimesheets) {
                const jobIdStr = st.jobId.toString();
                const existing = signedJobs[jobIdStr];
                // submitted > signed: при наличии дубликатов (старые баг с
                // generateClientId на каждую подпись) сохраняем лучший статус,
                // иначе одна signed-запись могла бы перезатереть submitted.
                if (existing && existing.status === 'submitted') continue;
                signedJobs[jobIdStr] = {
                    id: st.jobId,
                    signerName: st.signerName,
                    createdAt: st.createdAt,
                    dataModified: false,
                    // Подпись снята (часы правили) — работа не подписана, пока не переподпишут.
                    status: st.needsResign ? 'needs_resign' : (st.status === 'submitted' ? 'submitted' : 'signed'),
                };
            }

            if (!isMountedRef.current) return;

            safeSetState(setWeekInfo)({
                weekNumber: timesheetService.getWeekNumber(date),
                weekStart: weekDates[0],
                weekEnd,
                signedJobs,
            });
        } catch (error) {
            console.error('[Timesheet] loadDate failed:', error);
            showError('Failed to load data', error.message);
        } finally {
            loadingRef.current = false;
            if (reloadAgainRef.current) {
                reloadAgainRef.current = false;
                // Тот же путь, что после sync: перечитать видимую дату.
                if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent(EVT_SYNC_COMPLETED, { detail: { reload: true } }));
            }
        }
    }, [pullWeek]);

    useEffect(() => {
        loadDate(timesheetService.toLocalDateString());
    }, [loadDate]);

    // После успешного sync → syncStatus в IDB обновился с pending на synced,
    // но React в таблице показывает старые жёлтые бейджи. Перечитываем
    // видимую дату чтобы цвета обновились. Дешево: два раза за save-сессию.
    useEffect(() => {
        const handler = () => {
            const d = currentDateRef.current;
            if (d) loadDate(d);
        };
        window.addEventListener(EVT_SYNC_COMPLETED, handler);
        return () => window.removeEventListener(EVT_SYNC_COMPLETED, handler);
    }, [loadDate]);

    // ─── Record CRUD ────────────────────────────────────────────────────────
    //
    // ОДНА КЛЕТКА — ОДНА ЗАПИСЬ. 10/2026 на проде два быстрых тапа по клавиатуре
    // («1», сразу «10») завели ДВЕ записи в одну клетку: второй тап пришёл, пока
    // первая сохранялась, и обработчик со старым state её не нашёл. На сервер
    // ушло 11 + 11 NW = 22 ч, а в клетке было видно одну запись. Поэтому:
    //   - запись в клетку идёт по очереди (withCellLock);
    //   - «добавить» в занятую клетку становится правкой той, что есть;
    //   - срезы записей обновляются сразу после сохранения, не дожидаясь рендера
    //     (putLocal), — следующий в очереди уже видит запись.

    const cellQueueRef = useRef(new Map());
    const withCellLock = useCallback((key, fn) => {
        const prev = cellQueueRef.current.get(key) || Promise.resolve();
        const run = prev.catch(() => {}).then(fn);
        const tail = run.catch(() => {});
        cellQueueRef.current.set(key, tail);
        tail.then(() => {
            if (cellQueueRef.current.get(key) === tail) cellQueueRef.current.delete(key);
        });
        return run;
    }, []);

    const putLocal = useCallback((saved) => {
        const upsert = (list) => (list.some(r => r.clientId === saved.clientId)
            ? list.map(r => (r.clientId === saved.clientId ? saved : r))
            : [...list, saved]);
        if (saved.workDate === currentDateRef.current) recordsRef.current = upsert(recordsRef.current);
        weekRecordsRef.current = upsert(weekRecordsRef.current);
        if (!isMountedRef.current) return;
        setRecords(prev => (saved.workDate === currentDateRef.current ? upsert(prev) : prev));
        setWeekRecords(prev => upsert(prev));
    }, []);

    /**
     * Живая запись клетки из свежих срезов (не из state рендера). Если на
     * устройстве остались старые дубли — берём последнюю правленую, ту же, что
     * показывает грид.
     */
    const findCellRecord = useCallback((employeeId, jobId, date = currentDateRef.current) => {
        let best = null;
        for (const r of [...recordsRef.current, ...weekRecordsRef.current]) {
            if (r.isDeleted || r.employeeId !== employeeId || r.jobId !== jobId || r.workDate !== date) continue;
            if (!best || String(r.updatedAt || '') > String(best.updatedAt || '')) best = r;
        }
        return best;
    }, []);

    const addRecord = useCallback((employeeId, jobId, hours, attendCode = '', jobCode = '', notes = '', options = {}) => {
        const date = currentDateRef.current;
        return withCellLock(`${employeeId}|${jobId}|${date}`, async () => {
            try {
                // Клетка уже занята (запись появилась, пока ждали очередь, или
                // вызывающий смотрел в старый state) — правим её, вторую не заводим.
                const existing = findCellRecord(employeeId, jobId, date);
                if (existing) {
                    const saved = await timesheetService.saveTimesheetRecord({
                        ...existing,
                        hours,
                        attendCode: attendCode || existing.attendCode || '',
                        jobCode: jobCode || existing.jobCode || '',
                        notes: notes || existing.notes || '',
                    }, { validateLimit: !options.skipLimitCheck });
                    putLocal(saved);
                    return saved;
                }

                // Ночь наследуется ПО НЕДЕЛЕ: если у сотрудника на этой работе уже есть МОЙ
                // день с 'Night work', новый день с часами получает код автоматически. Так
                // ночная смена держится всю неделю БЕЗ пустых 0ч-записей наперёд. Смотрим
                // только на свои записи (чужую ночь другого формана не наследуем); явно
                // переданный код всегда приоритетнее.
                const myUserId = timesheetService.getCurrentUserMeta().userId;
                const inheritedNight = !attendCode && hours > 0 && weekRecordsRef.current.some(r =>
                    r.employeeId === employeeId && r.jobId === jobId && !r.isDeleted && hasNight(r.attendCode)
                    && (myUserId == null || Number(r.createdByUserId) === Number(myUserId))
                );
                const record = {
                    clientId: timesheetService.generateClientId(),
                    employeeId,
                    jobId,
                    workDate: date,
                    hours,
                    attendCode: inheritedNight ? 'Night work' : attendCode,
                    jobCode,
                    notes,
                    status: 'draft',
                };
                // skipLimitCheck=true — когда UI уже проверил лимит (экономит 1 IDB tx).
                const saved = await timesheetService.saveTimesheetRecord(record, {
                    validateLimit: !options.skipLimitCheck,
                });
                putLocal(saved);
                return saved;
            } catch (error) {
                console.error('[Timesheet] addRecord failed:', error);
                if (error.code !== 'DAILY_LIMIT_EXCEEDED') {
                    showError('Failed to save record', error.message);
                }
                throw error;
            }
        });
    }, [withCellLock, findCellRecord, putLocal]);

    const updateRecord = useCallback(async (clientId, hours, attendCode = '', jobCode = '', notes = '', options = {}) => {
        // Сначала пробуем найти в свежем срезе (быстро, без IDB tx).
        // В IDB идём только если запись не в текущей выборке — редкий кейс.
        const found = recordsRef.current.find(r => r.clientId === clientId)
            || weekRecordsRef.current.find(r => r.clientId === clientId)
            || await timesheetService.getTimesheetRecordById(clientId);
        if (!found) {
            const error = new Error(`Record ${clientId} not found`);
            console.error('[Timesheet] updateRecord failed:', error);
            showError('Failed to update record', error.message);
            throw error;
        }
        return withCellLock(`${found.employeeId}|${found.jobId}|${found.workDate}`, async () => {
            try {
                const existing = recordsRef.current.find(r => r.clientId === clientId)
                    || weekRecordsRef.current.find(r => r.clientId === clientId)
                    || found;
                const saved = await timesheetService.saveTimesheetRecord({
                    ...existing,
                    hours,
                    attendCode,
                    jobCode,
                    notes,
                    // Ответ на спор/reject: сервер и так снимет rejected при пересохранении,
                    // а здесь клетка сразу перестаёт быть красной.
                    ...(options.clearRejection ? { status: 'draft' } : {}),
                }, { validateLimit: !options.skipLimitCheck });
                putLocal(saved);
                return saved;
            } catch (error) {
                console.error('[Timesheet] updateRecord failed:', error);
                if (error.code !== 'DAILY_LIMIT_EXCEEDED') {
                    showError('Failed to update record', error.message);
                }
                throw error;
            }
        });
    }, [withCellLock, putLocal]);

    const removeRecord = useCallback(async (clientId) => {
        const found = recordsRef.current.find(r => r.clientId === clientId)
            || weekRecordsRef.current.find(r => r.clientId === clientId);
        const run = async () => {
            try {
                await timesheetService.deleteTimesheetRecord(clientId);
                recordsRef.current = recordsRef.current.filter(r => r.clientId !== clientId);
                weekRecordsRef.current = weekRecordsRef.current.filter(r => r.clientId !== clientId);
                if (!isMountedRef.current) return;
                setRecords(prev => prev.filter(r => r.clientId !== clientId));
                setWeekRecords(prev => prev.filter(r => r.clientId !== clientId));
            } catch (error) {
                console.error('[Timesheet] removeRecord failed:', error);
                showError('Failed to delete record', error.message);
                throw error;
            }
        };
        return found ? withCellLock(`${found.employeeId}|${found.jobId}|${found.workDate}`, run) : run();
    }, [withCellLock]);

    // ─── Daily Notes ────────────────────────────────────────────────────────

    const addDailyNote = useCallback(async (jobId, notes, employeeId) => {
        try {
            const date = currentDateRef.current;
            const existing = dailyNotesRef.current.find(n =>
                n.jobId === jobId && n.noteDate === date &&
                (employeeId ? n.employeeId === employeeId : !n.employeeId)
            );

            const note = existing
                ? { ...existing, notes }
                : {
                    clientId: timesheetService.generateClientId(),
                    jobId,
                    employeeId: employeeId || null,
                    noteDate: date,
                    notes,
                    status: 'draft',
                };

            const saved = await timesheetService.saveDailyJobNote(note);

            if (!isMountedRef.current) return saved;
            if (existing) {
                setDailyNotes(prev => prev.map(n => n.clientId === existing.clientId ? saved : n));
                setWeekDailyNotes(prev => prev.map(n => n.clientId === existing.clientId ? saved : n));
            } else {
                setDailyNotes(prev => [...prev, saved]);
                setWeekDailyNotes(prev => [...prev, saved]);
            }
            return saved;
        } catch (error) {
            console.error('[Timesheet] addDailyNote failed:', error);
            showError('Failed to save note', error.message);
            throw error;
        }
    }, []);

    // ─── Дневные атрибуты (работа, день): night work + cost-код дня ─────────

    /**
     * Upsert job-level заметки (employeeId NULL) на ЛЮБУЮ дату с patch'ем атрибутов
     * {isNight?, dayJobCode?}. Текст заметки не трогает. Ищет существующую в IDB
     * (byDate индекс) — надёжно для дат вне текущего дня (автозаполнение недели).
     */
    const setDayAttributes = useCallback(async (jobId, date, patch) => {
        const dayNotes = await timesheetService.getDailyJobNotesByDate(date);
        const existing = dayNotes.find(n => n.jobId === jobId && !n.employeeId && !n.tempId && !n.isDeleted);
        const base = existing || {
            clientId: timesheetService.generateClientId(),
            jobId,
            employeeId: null,
            noteDate: date,
            notes: '',
            status: 'draft',
        };
        const saved = await timesheetService.saveDailyJobNote({ ...base, ...patch });

        if (!isMountedRef.current) return saved;
        const upsert = prev => {
            const i = prev.findIndex(n => n.clientId === saved.clientId);
            return i >= 0 ? prev.map(n => n.clientId === saved.clientId ? saved : n) : [...prev, saved];
        };
        if (date === currentDateRef.current) setDailyNotes(upsert);
        setWeekDailyNotes(upsert);
        return saved;
    }, []);

    /**
     * Ночная смена бригады (работа+день). Включение автоматически проставляет ночь
     * на все ОСТАВШИЕСЯ дни этой недели (по воскресенье) — можно выключить любой
     * день вручную (выключение трогает только этот день).
     */
    const setNightWork = useCallback(async (jobId, date, on) => {
        const fmt = (dt) => `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
        const dates = [date];
        if (on) {
            const d = new Date(date + 'T00:00:00');
            const dow = d.getDay() === 0 ? 7 : d.getDay(); // 1=Mon..7=Sun
            for (let i = 1; i <= 7 - dow; i++) {
                const x = new Date(d);
                x.setDate(d.getDate() + i);
                dates.push(fmt(x));
            }
        }
        for (const dd of dates) {
            await setDayAttributes(jobId, dd, { isNight: !!on });
        }
    }, [setDayAttributes]);

    /** Cost-код дня (работа+день) из каталога внеш. JobCode — всем сотрудникам дня. */
    const setDayJobCode = useCallback(async (jobId, date, code) => {
        return setDayAttributes(jobId, date, { dayJobCode: code || null });
    }, [setDayAttributes]);

    /**
     * Влить изменения записей в оба состояния после мультидневной операции.
     * ВАЖНО: `records` — это записи ТОЛЬКО текущего дня; записи других дней туда
     * попадать не должны (иначе грид покажет чужой день как сегодняшний).
     * `weekRecords` — вся неделя.
     */
    const commitRecordChanges = useCallback((touched = [], removed = []) => {
        const removedSet = new Set(removed);
        const today = currentDateRef.current;
        const merge = (prev, list) => {
            const map = new Map(prev.filter(r => !removedSet.has(r.clientId)).map(r => [r.clientId, r]));
            for (const t of list) map.set(t.clientId, map.has(t.clientId) ? { ...map.get(t.clientId), ...t } : t);
            return [...map.values()];
        };
        setWeekRecords(prev => merge(prev, touched));
        setRecords(prev => merge(prev, touched.filter(t => t.workDate === today)));
    }, []);

    /**
     * Ночная смена — ПЕРСОНАЛЬНАЯ (attendCode='Night work' на записи сотрудника).
     * Ночь = МОДИФИКАТОР ОТРАБОТАННЫХ ЧАСОВ, а не «день без часов»: пустых записей
     * (0 часов + Night work) не создаём — они уезжали в синк и в payroll как мусорные
     * нулевые строки. Протяжка помечает только СУЩЕСТВУЮЩИЕ дни недели (часы не трогаем);
     * на будущие дни код наследуется автоматически в момент ввода часов (см. addRecord →
     * inheritWeekNight). Снятие/замена — clearEmployeeNight / правка ячейки.
     */
    const propagateEmployeeNight = useCallback(async (employeeId, jobId, fromDate) => {
        const fmt = (dt) => `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
        const d = new Date(fromDate + 'T00:00:00');
        const dow = d.getDay() === 0 ? 7 : d.getDay(); // 1=Mon..7=Sun
        const dates = [];
        for (let i = 1; i <= 7 - dow; i++) {
            const x = new Date(d);
            x.setDate(d.getDate() + i);
            dates.push(fmt(x));
        }
        // Помечаем ТОЛЬКО свои записи: у сотрудника на этот день может быть запись
        // другого формана — её трогать нельзя (это не наш день/ночь).
        const myUserId = timesheetService.getCurrentUserMeta().userId;
        const touched = [];
        for (const dd of dates) {
            const existing = weekRecordsRef.current.find(r =>
                r.employeeId === employeeId && r.jobId === jobId && r.workDate === dd && !r.isDeleted
                && (myUserId == null || Number(r.createdByUserId) === Number(myUserId))
            );
            // Дня ещё нет — НЕ создаём пустышку: код навесится сам при вводе часов.
            if (!existing || hasNight(existing.attendCode)) continue;
            const saved = await timesheetService.saveTimesheetRecord(
                { ...existing, attendCode: withNight(existing.attendCode) },
                { validateLimit: false },
            );
            touched.push(saved);
        }
        if (!isMountedRef.current || !touched.length) return touched.length;
        commitRecordChanges(touched, []);
        return touched.length;
    }, [commitRecordChanges]);

    /**
     * Обратная к propagateEmployeeNight: снять авто-протянутую ночную смену с
     * ОСТАВШИХСЯ дней недели этого сотрудника по работе, когда на исходном дне ночь
     * сняли/сменили (в т.ч. по ошибке). Пустой протянутый плейсхолдер (0 часов, без
     * заметки) — удаляем; день с реальными часами/заметкой — оставляем, но снимаем
     * ночной код. Исходный день здесь НЕ трогаем — он обрабатывается вызывающим.
     */
    const clearEmployeeNight = useCallback(async (employeeId, jobId, fromDate) => {
        const fmt = (dt) => `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
        const d = new Date(fromDate + 'T00:00:00');
        const dow = d.getDay() === 0 ? 7 : d.getDay();
        const dates = [];
        for (let i = 1; i <= 7 - dow; i++) {
            const x = new Date(d);
            x.setDate(d.getDate() + i);
            dates.push(fmt(x));
        }
        // Откатываем ТОЛЬКО свои записи — чужую ночь другого формана не трогаем.
        const myUserId = timesheetService.getCurrentUserMeta().userId;
        const touched = [];
        const removed = [];
        for (const dd of dates) {
            const existing = weekRecordsRef.current.find(r =>
                r.employeeId === employeeId && r.jobId === jobId && r.workDate === dd && !r.isDeleted
                && (myUserId == null || Number(r.createdByUserId) === Number(myUserId))
            );
            if (!existing || !hasNight(existing.attendCode)) continue;
            // Другие коды этого дня (уволен и т.п.) остаются.
            const rest = withoutNight(existing.attendCode);
            const hasData = rest || Number(existing.hours) > 0 || (existing.notes && String(existing.notes).trim() !== '');
            if (hasData) {
                const saved = await timesheetService.saveTimesheetRecord(
                    { ...existing, attendCode: rest },
                    { validateLimit: false },
                );
                touched.push(saved);
            } else {
                await timesheetService.deleteTimesheetRecord(existing.clientId);
                removed.push(existing.clientId);
            }
        }
        if (!isMountedRef.current || (!touched.length && !removed.length)) return touched.length + removed.length;
        commitRecordChanges(touched, removed);
        return touched.length + removed.length;
    }, [commitRecordChanges]);

    /**
     * Применить attendance-код сразу к НЕСКОЛЬКИМ дням недели (мультивыбор в редакторе
     * ячейки / Employee Notes). Правило создания записи там, где её нет:
     *   - absence-код (NC/EX/UN) → создаём 0ч-запись: прогул без часов валиден и нужен payroll;
     *   - Night work → НЕ создаём (ночь = модификатор часов, не отсутствие). Код навесится
     *     сам при вводе часов (наследование по неделе в addRecord);
     *   - пустой код → снимаем: запись без часов/заметки удаляем, иначе просто чистим код.
     * Трогаем ТОЛЬКО свои записи (чужие часы этого сотрудника у другого формана — не наши).
     *
     * @returns {Promise<number>} сколько дней реально затронуто
     */
    const setAttendCodeForDates = useCallback(async (employeeId, jobId, code, dates) => {
        if (!employeeId || !jobId || !Array.isArray(dates) || !dates.length) return 0;
        const myUserId = timesheetService.getCurrentUserMeta().userId;
        const isMine = (r) => myUserId == null || Number(r.createdByUserId) === Number(myUserId);
        const isNight = code === 'Night work';
        const touched = [];
        const removed = [];

        for (const dd of dates) {
            const existing = weekRecordsRef.current.find(r =>
                r.employeeId === employeeId && r.jobId === jobId && r.workDate === dd
                && !r.isDeleted && isMine(r)
            );
            if (existing) {
                if ((existing.attendCode || '') === (code || '')) continue;
                const hasData = Number(existing.hours) > 0 || (existing.notes && String(existing.notes).trim() !== '');
                if (!code && !hasData) {
                    await timesheetService.deleteTimesheetRecord(existing.clientId);
                    removed.push(existing.clientId);
                } else {
                    const saved = await timesheetService.saveTimesheetRecord(
                        { ...existing, attendCode: code || '' },
                        { validateLimit: false },
                    );
                    touched.push(saved);
                }
            } else {
                if (!code || isNight) continue; // нечего снимать / не плодим 0ч-ночь
                const saved = await timesheetService.saveTimesheetRecord({
                    clientId: timesheetService.generateClientId(),
                    employeeId,
                    jobId,
                    workDate: dd,
                    hours: 0,
                    attendCode: code,
                    jobCode: '',
                    notes: '',
                    status: 'draft',
                }, { validateLimit: false });
                touched.push(saved);
            }
        }
        if (!isMountedRef.current || (!touched.length && !removed.length)) return 0;
        commitRecordChanges(touched, removed);
        return touched.length + removed.length;
    }, [commitRecordChanges]);

    // ─── Sign Timesheet (local only) ────────────────────────────────────────

    const signTimesheet = useCallback(async (jobId, weekEndingDate, payload = {}) => {
        try {
            // payload из SignTimesheetModal → сохраняем в запись подписи as-is,
            // sync пошлёт на сервер целиком (stripLocalOnlyFields пропускает новые поля).
            //   signatureImage/signerName   = заказчик (необязательные)
            //   foremanSignatureImage        = подпись форемана (по умолчанию авто из имени)
            //   items (+per-item note), foremanComment, dayNarratives
            const items = Array.isArray(payload.items) ? payload.items : [];
            const foremanComment = payload.foremanComment || '';
            const dayNarratives = payload.dayNarratives || null;
            const customerSignatureImage = payload.customerSignatureImage || '';
            const signerName = payload.signerName || '';
            const foremanSignatureImage = payload.foremanSignatureImage || '';
            const foremanName = (payload.foremanName || '').trim();
            // Галочка «Email me the signed PDF»: сервер шлёт письмо подписавшему,
            // когда примет запись. Разовая — после отправки sync её снимает.
            const emailPdf = !!payload.emailPdf;
            const emailPdfScope = payload.emailPdfScope || 'week';
            // Подписывается один день, неделя видна целиком.
            const signedDay = payload.signedDay || null;
            // Метка этой подписи: сервер ведёт по ней журнал подписей (подписывают
            // и каждый день) — повторная отправка той же подписи дубля не даст.
            const signedAt = new Date().toISOString();

            // Обновляем существующую запись подписи если она уже есть —
            // иначе плодим дубликаты в IDB и getSignedTimesheetsByWeek
            // потом возвращает несогласованный результат.
            const existing = await timesheetService.getSignedTimesheetsByWeek(weekEndingDate);
            const prior = existing.find(s => s.jobId === jobId);
            const signed = prior
                ? {
                    ...prior,
                    signatureImage: customerSignatureImage,
                    signerName,
                    foremanSignatureImage,
                    items,
                    foremanComment,
                    dayNarratives,
                    emailPdf,
                    emailPdfScope,
                    signedAt,
                    signedDay,
                    // Переподпись снимает флаг «нужно переподписать».
                    needsResign: false,
                    resignReason: null,
                    invalidatedAt: null,
                    // Не понижаем submitted → signed
                    status: prior.status === 'submitted' ? 'submitted' : 'signed',
                }
                : {
                    clientId: timesheetService.generateClientId(),
                    jobId,
                    weekEndingDate,
                    signatureImage: customerSignatureImage,
                    signerName,
                    foremanSignatureImage,
                    items,
                    foremanComment,
                    dayNarratives,
                    emailPdf,
                    emailPdfScope,
                    signedAt,
                    signedDay,
                    needsResign: false,
                    status: 'signed',
                    // Имя форемана = автор записи. Бэкенд fillAuthor использует его для
                    // createdByName при создании (пустое — упадёт на JWT-имя сервера).
                    ...(foremanName ? { createdByName: foremanName } : {}),
                };
            const saved = await timesheetService.saveSignedTimesheet(signed);

            // Оптимистично проставляем status='signed' всем timesheet_record
            // этой недели+job — UI получает signed-флаг СРАЗУ, не ждёт 15-сек
            // sync round-trip к серверу. Сервер при propagateSignedStatusToRecords
            // сделает то же самое; pull-merge тихо подтвердит без видимого скачка.
            await timesheetService.markRecordsSignedForJobWeek(jobId, weekEndingDate, signedDay);

            if (!isMountedRef.current) return { success: true };

            // То же оптимистичное обновление в React state — иначе пользователь
            // должен был бы дожидаться следующего loadDate/EVT_SYNC_COMPLETED.
            const allowedPriorStatuses = new Set(['draft', 'submitted', 'rejected']);
            const optimisticPatch = (rec) => {
                if (rec.jobId !== jobId) return rec;
                if (signedDay && rec.workDate !== signedDay) return rec;
                if (rec.isDeleted) return rec;
                if (!allowedPriorStatuses.has(rec.status)) return rec;
                return { ...rec, status: 'signed' };
            };
            setRecords(prev => prev.map(optimisticPatch));
            setWeekRecords(prev => prev.map(optimisticPatch));

            setWeekInfo(prev => {
                if (!prev) return prev;
                const existingInfo = prev.signedJobs?.[jobId.toString()];
                // Не понижаем submitted → signed в оптимистическом обновлении
                if (existingInfo?.status === 'submitted') return prev;
                return {
                    ...prev,
                    signedJobs: {
                        ...prev.signedJobs,
                        [jobId.toString()]: {
                            id: jobId,
                            signerName: signerName || '',
                            createdAt: saved.createdAt,
                            dataModified: false,
                            status: 'signed',
                        },
                    },
                };
            });
            return { success: true };
        } catch (error) {
            console.error('[Timesheet] signTimesheet failed:', error);
            showError('Failed to sign', error.message);
            return { success: false, error: error.message };
        }
    }, []);

    const signAllTimesheets = useCallback(async (jobIds, weekEndingDate, payload = {}) => {
        let successCount = 0;
        let failCount = 0;
        // Один и тот же payload (подписи/items/narratives/comment) сохраняется в
        // signed-запись каждой работы этого customer'а.
        for (const jobId of jobIds) {
            const result = await signTimesheet(jobId, weekEndingDate, payload);
            if (result.success) successCount++;
            else failCount++;
        }
        return { successCount, failCount };
    }, [signTimesheet]);

    // ─── Submit Timesheet ───────────────────────────────────────────────────

    const submitTimesheet = useCallback(async (jobId, weekEndingDate) => {
        try {
            const signed = await timesheetService.getSignedTimesheetsByWeek(weekEndingDate);
            const existing = signed.find(s => s.jobId === jobId);

            if (existing) {
                await timesheetService.saveSignedTimesheet({ ...existing, status: 'submitted' });
            } else {
                await timesheetService.saveSignedTimesheet({
                    clientId: timesheetService.generateClientId(),
                    jobId,
                    weekEndingDate,
                    signatureImage: '',
                    signerName: '',
                    status: 'submitted',
                });
            }

            if (!isMountedRef.current) return { success: true };
            setWeekInfo(prev => {
                if (!prev) return prev;
                const current = prev.signedJobs[jobId.toString()] || {};
                return {
                    ...prev,
                    signedJobs: {
                        ...prev.signedJobs,
                        [jobId.toString()]: { ...current, id: jobId, status: 'submitted' },
                    },
                };
            });
            return { success: true };
        } catch (error) {
            console.error('[Timesheet] submitTimesheet failed:', error);
            showError('Failed to submit', error.message);
            return { success: false, error: error.message };
        }
    }, []);

    const submitAllTimesheets = useCallback(async (jobIds, weekEndingDate) => {
        let successCount = 0;
        let failCount = 0;
        for (const jobId of jobIds) {
            const result = await submitTimesheet(jobId, weekEndingDate);
            if (result.success) successCount++;
            else failCount++;
        }
        return { successCount, failCount };
    }, [submitTimesheet]);

    // ─── Selected Employees / Jobs ──────────────────────────────────────────

    const setSelectedEmployees = useCallback(async (employeeIds) => {
        try {
            const unique = [...new Set(employeeIds)];
            await timesheetService.saveSelectedEmployeesForWeek(currentDateRef.current, unique);
            if (isMountedRef.current) setSelectedEmployeeIdsState(unique);
            pullWeek(currentDateRef.current, unique, true);
        } catch (error) {
            console.error('[Timesheet] setSelectedEmployees failed:', error);
            showError('Failed to save employee selection', error.message);
        }
    }, [pullWeek]);

    const setSelectedJobs = useCallback(async (jobIds) => {
        try {
            const unique = [...new Set(jobIds)];
            await timesheetService.saveSelectedJobsForWeek(currentDateRef.current, unique);
            if (isMountedRef.current) setSelectedJobIdsState(unique);
        } catch (error) {
            console.error('[Timesheet] setSelectedJobs failed:', error);
            showError('Failed to save job selection', error.message);
        }
    }, []);

    /** Есть ли approved records в указанном списке? Approved = payroll-финал,
     *  их нельзя soft-delete — сервер отобьёт APPROVED_LOCKED, а с точки зрения
     *  бизнеса удаление approved часов = скрытые оплаченные часы = fraud risk. */
    const hasApprovedRecords = useCallback((records) => {
        return records.some(r => !r.isDeleted && r.status === 'approved');
    }, []);

    const showApprovedBlockedError = useCallback((context) => {
        Swal.fire({
            icon: 'error',
            title: 'Locked — approved for payroll',
            html: `${context}<br><br>Hours already sent to ADP cannot be removed or cleared. Ask the office to <b>unapprove</b> them first in the admin panel.`,
            confirmButtonText: 'Got it',
        });
    }, []);

    /**
     * Сняли людей в окне выбора, юзер подтвердил «удалить часы»: удаляем СВОИ
     * часы этих людей за неделю — как удаление строки крестиком. Список
     * сохраняет setSelectedEmployees. Без этого часы оставались в базе, а
     * человек при следующей загрузке снова появлялся в листе.
     */
    const deleteWeekHoursOf = useCallback(async (employeeIds) => {
        const drop = new Set(employeeIds);
        const myUserId = timesheetService.getCurrentUserMeta().userId;
        const isMine = (r) => myUserId == null || Number(r.createdByUserId) === Number(myUserId);
        for (const wr of weekRecordsRef.current) {
            if (drop.has(wr.employeeId) && isMine(wr) && !wr.isDeleted) {
                await timesheetService.deleteTimesheetRecord(wr.clientId);
            }
        }
        if (!isMountedRef.current) return;
        const gone = (r) => drop.has(r.employeeId) && isMine(r);
        setRecords(prev => prev.filter(r => !gone(r)));
        setWeekRecords(prev => prev.filter(r => !gone(r)));
    }, []);

    const removeEmployeeFromDay = useCallback(async (employeeId) => {
        try {
            // Убираем сотрудника ТОЛЬКО из своего листа и удаляем ТОЛЬКО свои записи.
            // Чужие часы этого человека (другой форман, другая работа) не трогаем —
            // иначе удаление из своего листа сносило чужие данные из общей базы.
            const myUserId = timesheetService.getCurrentUserMeta().userId;
            const isMine = (r) => myUserId == null || Number(r.createdByUserId) === Number(myUserId);

            // Блок: не удаляем сотрудника если у него есть СВОИ approved часы на неделе
            const empRecords = weekRecordsRef.current.filter(r => r.employeeId === employeeId && isMine(r));
            if (hasApprovedRecords(empRecords)) {
                showApprovedBlockedError(
                    'This worker has hours that have been approved for payroll.'
                );
                return;
            }

            const newIds = selectedEmployeeIdsRef.current.filter(id => id !== employeeId);
            await timesheetService.saveSelectedEmployeesForWeek(currentDateRef.current, newIds);

            for (const wr of weekRecordsRef.current) {
                if (wr.employeeId === employeeId && isMine(wr)) {
                    await timesheetService.deleteTimesheetRecord(wr.clientId);
                }
            }

            if (!isMountedRef.current) return;
            const dropMine = (r) => r.employeeId === employeeId && isMine(r);
            setSelectedEmployeeIdsState(newIds);
            setRecords(prev => prev.filter(r => !dropMine(r)));
            setWeekRecords(prev => prev.filter(r => !dropMine(r)));
        } catch (error) {
            console.error('[Timesheet] removeEmployeeFromDay failed:', error);
            showError('Failed to remove employee', error.message);
        }
    }, [hasApprovedRecords, showApprovedBlockedError]);

    const removeJobFromList = useCallback(async (jobId) => {
        try {
            // Как и с сотрудником: убираем работу из своего листа и удаляем ТОЛЬКО свои
            // записи по ней. Часы других форманов на этой же работе не трогаем.
            const myUserId = timesheetService.getCurrentUserMeta().userId;
            const isMine = (r) => myUserId == null || Number(r.createdByUserId) === Number(myUserId);

            // Блок: не удаляем работу если у неё есть СВОИ approved часы в неделе
            const jobRecords = weekRecordsRef.current.filter(r => r.jobId === jobId && isMine(r));
            if (hasApprovedRecords(jobRecords)) {
                showApprovedBlockedError(
                    'This job has hours that have been approved for payroll.'
                );
                return;
            }

            const newIds = selectedJobIdsRef.current.filter(id => id !== jobId);
            await timesheetService.saveSelectedJobsForWeek(currentDateRef.current, newIds);

            for (const wr of weekRecordsRef.current) {
                if (wr.jobId === jobId && isMine(wr)) {
                    await timesheetService.deleteTimesheetRecord(wr.clientId);
                }
            }

            if (!isMountedRef.current) return;
            const dropMine = (r) => r.jobId === jobId && isMine(r);
            setSelectedJobIdsState(newIds);
            setRecords(prev => prev.filter(r => !dropMine(r)));
            setWeekRecords(prev => prev.filter(r => !dropMine(r)));
        } catch (error) {
            console.error('[Timesheet] removeJobFromList failed:', error);
            showError('Failed to remove job', error.message);
        }
    }, [hasApprovedRecords, showApprovedBlockedError]);

    // ─── Clear Week / Day ───────────────────────────────────────────────────

    const clearWeek = useCallback(async () => {
        try {
            // Чистим ТОЛЬКО свои записи: в локальной базе лежат и часы других форманов
            // по этим же сотрудникам — «Clear week» не должен сносить чужую работу.
            const myUserId = timesheetService.getCurrentUserMeta().userId;
            const isMine = (r) => myUserId == null || Number(r.createdByUserId) === Number(myUserId);
            const mine = weekRecordsRef.current.filter(isMine);
            if (hasApprovedRecords(mine)) {
                showApprovedBlockedError(
                    'This week contains hours that have been approved for payroll.'
                );
                return;
            }
            for (const wr of mine) {
                await timesheetService.deleteTimesheetRecord(wr.clientId);
            }
            if (!isMountedRef.current) return;
            setRecords(prev => prev.filter(r => !isMine(r)));
            setWeekRecords(prev => prev.filter(r => !isMine(r)));
            setDailyNotes([]);
            setWeekDailyNotes([]);
        } catch (error) {
            console.error('[Timesheet] clearWeek failed:', error);
            showError('Failed to clear week', error.message);
        }
    }, [hasApprovedRecords, showApprovedBlockedError]);

    const clearDay = useCallback(async () => {
        try {
            const date = currentDateRef.current;
            // Как и clearWeek — только свои записи этого дня.
            const myUserId = timesheetService.getCurrentUserMeta().userId;
            const isMine = (r) => myUserId == null || Number(r.createdByUserId) === Number(myUserId);
            const dayRecords = weekRecordsRef.current.filter(r => r.workDate === date && isMine(r));
            if (hasApprovedRecords(dayRecords)) {
                showApprovedBlockedError(
                    'This day contains hours that have been approved for payroll.'
                );
                return;
            }
            for (const r of dayRecords) {
                await timesheetService.deleteTimesheetRecord(r.clientId);
            }
            if (!isMountedRef.current) return;
            const dropMine = (r) => r.workDate === date && isMine(r);
            setRecords(prev => prev.filter(r => !dropMine(r)));
            setWeekRecords(prev => prev.filter(r => !dropMine(r)));
        } catch (error) {
            console.error('[Timesheet] clearDay failed:', error);
            showError('Failed to clear day', error.message);
        }
    }, [hasApprovedRecords, showApprovedBlockedError]);

    const deleteRecordsByClientIds = useCallback(async (clientIds) => {
        if (!clientIds || clientIds.length === 0) return;
        try {
            const idSet = new Set(clientIds);
            for (const id of clientIds) {
                await timesheetService.deleteTimesheetRecord(id);
            }
            if (!isMountedRef.current) return;
            setRecords(prev => prev.filter(r => !idSet.has(r.clientId)));
            setWeekRecords(prev => prev.filter(r => !idSet.has(r.clientId)));
        } catch (error) {
            console.error('[Timesheet] deleteRecordsByClientIds failed:', error);
            showError('Failed to delete records', error.message);
        }
    }, []);

    // ─── Calendar Data ──────────────────────────────────────────────────────

    const fetchCalendarData = useCallback(async (year, month) => {
        const startDate = `${year}-${String(month + 1).padStart(2, '0')}-01`;
        const lastDay = new Date(year, month + 1, 0).getDate();
        const endDate = `${year}-${String(month + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;

        const startWeekDates = timesheetService.getWeekDates(startDate);
        const endWeekDates = timesheetService.getWeekDates(endDate);
        const rangeStart = startWeekDates[0];
        const rangeEnd = endWeekDates[6];

        const allRecords = await timesheetService.getTimesheetRecordsByDateRange(rangeStart, rangeEnd);
        const signedTimesheets = await timesheetService.getAllSignedTimesheets();

        const calendarData = {};
        const allDates = new Set();

        const current = new Date(rangeStart + 'T00:00:00');
        const end = new Date(rangeEnd + 'T00:00:00');
        while (current <= end) {
            allDates.add(timesheetService.toLocalDateString(current));
            current.setDate(current.getDate() + 1);
        }

        for (const dateStr of allDates) {
            const dayRecords = allRecords.filter(r => r.workDate === dateStr && !r.isDeleted);
            const totalHours = dayRecords.reduce((sum, r) => sum + r.hours, 0);

            const weekEnd = timesheetService.getWeekEndingDate(dateStr);
            const weekSigned = signedTimesheets.filter(s => s.weekEndingDate === weekEnd && !s.isDeleted && !s.needsResign);
            let weekStatus = 'none';
            if (weekSigned.some(s => s.status === 'submitted')) weekStatus = 'submitted';
            else if (weekSigned.length > 0) weekStatus = 'signed';

            calendarData[dateStr] = {
                hasRecords: dayRecords.length > 0,
                weekStatus,
                totalHours,
            };
        }

        return calendarData;
    }, []);

    // ─── Copy last week's lists ─────────────────────────────────────────────

    /**
     * Люди и работы прошлой недели — то, что «Copy last week» подтянет в
     * открытую. Сохранённый лист той недели плюс те, у кого там есть МОИ часы:
     * если ту неделю на этом айпаде не открывали, лист мог не сохраниться.
     */
    const getPreviousWeekSelections = useCallback(async () => {
        const monday = new Date(timesheetService.getWeekDates(currentDateRef.current)[0] + 'T00:00:00');
        monday.setDate(monday.getDate() - 7);
        const prevMonday = timesheetService.toLocalDateString(monday);
        const prevDates = timesheetService.getWeekDates(prevMonday);

        const [empIds, jobIds, recs] = await Promise.all([
            timesheetService.getSelectedEmployeesWithFallback(prevMonday),
            timesheetService.getSelectedJobsWithFallback(prevMonday),
            timesheetService.getTimesheetRecordsByWeek(prevMonday),
        ]);
        const myUserId = timesheetService.getCurrentUserMeta().userId;
        const mine = recs.filter(r => !r.isDeleted && r.hours > 0
            && (myUserId == null || Number(r.createdByUserId) === Number(myUserId)));

        return {
            weekStart: prevDates[0],
            weekEnd: prevDates[6],
            employeeIds: [...new Set([...empIds, ...mine.map(r => r.employeeId)])],
            jobIds: [...new Set([...jobIds, ...mine.map(r => r.jobId)])],
        };
    }, []);

    /** Дописать людей и работы в лист открытой недели; уже стоящие не трогаем. */
    const addToSelections = useCallback(async (employeeIds, jobIds) => {
        try {
            const emp = [...new Set([...selectedEmployeeIdsRef.current, ...employeeIds])];
            const jobs = [...new Set([...selectedJobIdsRef.current, ...jobIds])];
            await timesheetService.saveSelectedEmployeesForWeek(currentDateRef.current, emp);
            await timesheetService.saveSelectedJobsForWeek(currentDateRef.current, jobs);
            pullWeek(currentDateRef.current, emp, true);
            if (!isMountedRef.current) return;
            setSelectedEmployeeIdsState(emp);
            setSelectedJobIdsState(jobs);
        } catch (error) {
            console.error('[Timesheet] addToSelections failed:', error);
            showError('Failed to copy last week', error.message);
        }
    }, [pullWeek]);

    // ─── Copy previous day's hours ──────────────────────────────────────────

    /**
     * Часы из прошлого дня в открытый: entries = [{ employeeId, jobId, hours }].
     * Ячейки, где уже есть запись, вызывающий отсеял; здесь ещё раз проверяем
     * лимит 24 ч на человека с учётом уже вписанного за день.
     * Ночь отдельно не копируем: addRecord сам наследует Night work по неделе.
     */
    const copyEntriesToCurrentDay = useCallback(async (entries) => {
        const date = currentDateRef.current;
        const myUserId = timesheetService.getCurrentUserMeta().userId;
        const dayTotals = new Map();
        for (const r of recordsRef.current) {
            if (!r.isDeleted) dayTotals.set(r.employeeId, (dayTotals.get(r.employeeId) || 0) + (r.hours || 0));
        }
        // Ночь по неделе — как в addRecord: у человека на этой работе уже есть
        // мой день с Night work → новый день с часами тоже ночной.
        const nightKeys = new Set(weekRecordsRef.current
            .filter(r => !r.isDeleted && hasNight(r.attendCode)
                && (myUserId == null || Number(r.createdByUserId) === Number(myUserId)))
            .map(r => `${r.employeeId}|${r.jobId}`));

        // Лист бывает длинным: пишем пачкой и обновляем сетку ОДИН раз, а не
        // перерисовываем её после каждой записи.
        const saved = [];
        const overLimit = [];
        let hours = 0;
        try {
            for (const e of entries) {
                // Клетку успели заполнить, пока шёл диалог, — не заводим вторую запись.
                if (findCellRecord(e.employeeId, e.jobId, date)) continue;
                const total = (dayTotals.get(e.employeeId) || 0) + e.hours;
                if (total > 24) {
                    overLimit.push(e);
                    continue;
                }
                const rec = await timesheetService.saveTimesheetRecord({
                    clientId: timesheetService.generateClientId(),
                    employeeId: e.employeeId,
                    jobId: e.jobId,
                    workDate: date,
                    hours: e.hours,
                    attendCode: nightKeys.has(`${e.employeeId}|${e.jobId}`) ? 'Night work' : '',
                    jobCode: '',
                    notes: '',
                    status: 'draft',
                }, { validateLimit: false });
                saved.push(rec);
                // Срезы — сразу: следующий ввод в эту клетку должен её видеть.
                recordsRef.current = [...recordsRef.current, rec];
                weekRecordsRef.current = [...weekRecordsRef.current, rec];
                dayTotals.set(e.employeeId, total);
                hours += e.hours;
            }
        } finally {
            // Что успело записаться — показываем, даже если запись упала посередине.
            if (saved.length && isMountedRef.current) {
                setRecords(prev => [...prev, ...saved]);
                setWeekRecords(prev => [...prev, ...saved]);
            }
        }
        return { added: saved.length, hours, overLimit };
    }, [findCellRecord]);

    // ─── Week Status Checks ─────────────────────────────────────────────────

    // canLeaveCurrentWeek удалён: неподписанная неделя больше не мешает
    // уйти на другую. Подпись — необязательная опция.

    const isCurrentWeekSubmitted = useCallback(() => {
        if (!weekInfo?.signedJobs) return false;
        const jobsWithHours = new Set(
            weekRecordsRef.current
                .filter(r => !r.isDeleted && r.hours > 0)
                .map(r => r.jobId)
        );
        if (jobsWithHours.size === 0) return false;
        for (const jobId of jobsWithHours) {
            const info = weekInfo.signedJobs[jobId.toString()];
            if (!info || info.status !== 'submitted') return false;
        }
        return true;
    }, [weekInfo]);

    // checkPreviousWeekUnsubmitted удалён: подпись — необязательная опция,
    // проверять и напоминать про неподписанную прошлую неделю больше не нужно.

    // ─── Return ─────────────────────────────────────────────────────────────

    return {
        currentDate,
        records,
        recordsDate,
        weekRecords,
        dailyNotes,
        weekDailyNotes,
        selectedEmployeeIds,
        selectedJobIds,
        weekInfo,

        loadDate,
        addRecord,
        updateRecord,
        removeRecord,
        findCellRecord,
        addDailyNote,
        setDayAttributes,
        setNightWork,
        setDayJobCode,
        propagateEmployeeNight,
        clearEmployeeNight,
        setAttendCodeForDates,
        signTimesheet,
        signAllTimesheets,
        submitTimesheet,
        submitAllTimesheets,
        setSelectedEmployees,
        setSelectedJobs,
        removeEmployeeFromDay,
        deleteWeekHoursOf,
        removeJobFromList,
        clearWeek,
        clearDay,
        deleteRecordsByClientIds,
        fetchCalendarData,
        getPreviousWeekSelections,
        addToSelections,
        copyEntriesToCurrentDay,
        isCurrentWeekSubmitted,
    };
}

export default useTimesheetData;
